<template>
  <div class="project-map-page">
    <div
      v-if="loadProjectCoordinates"
      class="text-center project-page-spinner"
    >
      <b-spinner
        variant="primary"
        label="Loading..."
      />
    </div>
    <div
      v-else
      class="map-area-wrapper"
    >
      <div
        ref="mapDiv"
        class="map-container"
      />
    </div>
  </div>
</template>

<script>
import { loadArcgisModules, destroyArcgisView } from '@/utils/arcgisLoader'
import { mapGetters } from 'vuex'
import {
  BSpinner,
} from 'bootstrap-vue'

export default {
  components: {
    BSpinner,
  },
  data() {
    return {}
  },
  computed: {
    ...mapGetters({
      projectCoordinates: 'projects/projectCoordinates',
      loadProjectCoordinates: 'projects/loadProjectCoordinates',
    }),
    id() {
      return this.$route.params.id
    },
  },
  watch: {
    loadProjectCoordinates(val) {
      if (val === false) {
        this.$nextTick(() => this.initMap())
      }
    },
  },
  created() {
    this.view = null
    this.map = null
    this.polygonsLayer = null
  },
  mounted() {
    this.init()
    if (!this.loadProjectCoordinates) {
      this.initMap()
    }
  },
  beforeDestroy() {
    destroyArcgisView(this.view)
    this.view = null
    this.map = null
    this.polygonsLayer = null
  },
  methods: {
    async initMap() {
      const [Map, MapView, GraphicsLayer, Graphic] = await loadArcgisModules([
        'esri/Map',
        'esri/views/MapView',
        'esri/layers/GraphicsLayer',
        'esri/Graphic',
      ])
      if (!this.$refs.mapDiv) return
      destroyArcgisView(this.view)
      this.map = new Map({ basemap: 'satellite' })
      let center = [31.25, 31.06]
      const zoom = 16
      if (this.projectCoordinates && this.projectCoordinates.length > 0) {
        const proj = this.projectCoordinates[0]
        if (proj.fields_coordinates && proj.fields_coordinates.length > 0) {
          center = [Number(proj.fields_coordinates[0].longitude), Number(proj.fields_coordinates[0].latitude)]
        } else if (proj.technical_office_coordinates && proj.technical_office_coordinates.length > 0) {
          center = [Number(proj.technical_office_coordinates[0].longitude), Number(proj.technical_office_coordinates[0].latitude)]
        }
      }
      this.view = new MapView({
        container: this.$refs.mapDiv,
        map: this.map,
        center,
        zoom,
        popup: { dockEnabled: true, dockOptions: { position: 'bottom-right' } },
      })
      this.polygonsLayer = new GraphicsLayer()
      this.map.add(this.polygonsLayer)
      this.drawPolygons(Graphic)
    },
    drawPolygons(Graphic) {
      this.polygonsLayer.removeAll()
      if (!this.projectCoordinates) return
      this.projectCoordinates.forEach(project => {
        // Type configs
        [
          { label: 'Technical Office', coordsKey: 'technical_office_coordinates', color: project.type.color || '#22C55E' },
          { label: 'Fields', coordsKey: 'fields_coordinates', color: '#EF4444' },
        ].forEach(({ label, coordsKey, color }) => {
          const coordinates = project[coordsKey]
          if (coordinates && coordinates.length > 2) {
            const rings = [coordinates.map(c => [
              Number(c.longitude), Number(c.latitude),
            ])]
            // Use Esri color array: [r, g, b, a]
            const colorArr = color.startsWith('#')
              ? [
                parseInt(color.slice(1, 3), 16),
                parseInt(color.slice(3, 5), 16),
                parseInt(color.slice(5, 7), 16),
                0.4,
              ] : [0, 128, 0, 0.4]
            const symbol = {
              type: 'simple-fill',
              color: colorArr,
              outline: { color: [0, 0, 0, 0.7], width: 2 },
            }
            const polygon = {
              type: 'polygon',
              rings,
              spatialReference: { wkid: 4326 },
            }
            const graphic = new Graphic({
              geometry: polygon,
              symbol,
              attributes: {
                name: project.name,
                type: label,
                color,
                type_name: project.type && project.type.name_ar ? project.type.name_ar : '',
                project_id: project.project_id,
              },
              popupTemplate: {
                title: '{name}',
                content: `
                  <div>
                    <b>النوع:</b> {type}<br />
                  </div>
                `,
              },
            })
            this.polygonsLayer.add(graphic)
          }
        })
      })
    },
    init() {
      this.$store.dispatch('projects/projectCoordinates', { id: this.id })
    },
  },
}
</script>

<style scoped>
.project-map-page {
  background: #F5F7FA;
  border-radius: 12px;
  max-width: 100%;
  height: 100%;
  padding: 10px;
  margin: 0 auto;
}
.map-container {
  width: 100%;
  height: 500px;
  border-radius: 18px;
  box-shadow: 0 3px 24px #53636b1a;
  background: #e3e4e6;
}
.project-page-spinner {
  margin-top: 80px;
}
.map-area-wrapper {
  padding: 24px 0;
}
</style>
